/**
 * Cliente HTTP do app + os tipos que o servidor devolve (espelhando
 * src/servidor/app.ts — a mesma regra do painel/src/api.ts).
 */

export class NaoAutenticado extends Error {}

export class ErroApi extends Error {
  constructor(
    mensagem: string,
    readonly status: number,
  ) {
    super(mensagem);
  }
}

/* ⚠️ Sem isto, uma rede ruim deixava o fetch pendurado para sempre: o botão
   vira "Enviando…" e nunca volta, e "usar outro telefone" também trava (ele
   depende do mesmo `ocupado`) — a pessoa só sai fechando o app. 15s é generoso
   para um POST pequeno; rejeita como qualquer outro erro de rede, e o
   `finally` de `useAgir` já solta o botão sozinho. */
async function pedir<T>(caminho: string, corpo?: unknown): Promise<T> {
  const r = await fetch(`/api/app${caminho}`, {
    method: corpo === undefined ? 'GET' : 'POST',
    headers: corpo === undefined ? undefined : { 'content-type': 'application/json' },
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
    signal: AbortSignal.timeout(15_000),
  });
  if (r.status === 401) throw new NaoAutenticado();
  const dados = (await r.json().catch(() => null)) as { erro?: string } | null;
  if (!r.ok) throw new ErroApi(dados?.erro ?? `erro ${r.status}`, r.status);
  return dados as T;
}

// ---- tipos, espelhando o que o servidor devolve ----

export interface Sessao {
  nomeLoja: string;
  nomeDaMoeda: string;
  gatilhoCentavos: number;
  telefone: string;
  nome: string | null;
  saldoMoedas: number;
}

/**
 * A foto do prêmio, vinda do catálogo da loja. `null` = sem foto, e aí a tela
 * mantém o desenho hachurado.
 *
 * ⚠️ Nunca chega placeholder aqui: o servidor filtra `eh_placeholder`, porque
 * retângulo cinza gerado automaticamente parece defeito para a cliente.
 */
export interface PremioNaVitrine {
  id: string;
  nome: string;
  custoMoedas: number;
  capa: string | null;
}

/** Quando o saldo vence (validade ligada na loja). `dia` é `AAAA-MM-DD` no relógio da loja. */
export interface VencimentoDoSaldo {
  /** O ÚLTIMO dia em que as moedas valem. */
  dia: string;
  moedas: number;
  /** `true`: o saldo inteiro vence e cada compra renova. */
  renova: boolean;
}

export interface Carteira {
  nomeDaMoeda: string;
  saldoMoedas: number;
  /** Do saldo, o que ainda cumpre a carência da loja e não troca agora (F1). */
  emCarencia: number;
  faltaCentavos: number;
  /** `faltam` só vem com a opção "mostrar quanto falta" ligada na loja. */
  proxima: (PremioNaVitrine & { faltam?: number }) | null;
  /** Ausente = a loja não usa validade, ou não há nada a vencer. */
  vencimento?: VencimentoDoSaldo;
  destaques: PremioNaVitrine[];
}

/**
 * A cartela de carimbos, quando a loja fideliza por visita e não por valor.
 *
 * Vem na mesma rota `/carteira`: é a mesma pergunta ("o que eu tenho e o que
 * falta?"), com a resposta em visitas.
 */
export interface CarteiraCartela {
  modo: 'cartela';
  /** A cor da marca da loja — vira `--accent` na tela. */
  cor: string;
  cartela: {
    carimbos: number;
    alvo: number;
    faltam: number;
    premio: string;
    /** A frase pronta do servidor: "faltam 3 visitas". */
    comoSeLe: string;
  } | null;
}

export interface ItemLojinha {
  id: string;
  nome: string;
  custoMoedas: number;
  vigenciaFim: string | null;
  alcancavel: boolean;
  faltam: number;
  capa: string | null;
}

export interface Recompensa {
  id: string;
  nome: string;
  capa: string | null;
  descricao: string | null;
  custoMoedas: number;
  vigenciaFim: string | null;
  limitePorMes: number | null;
  lojas: string[] | null;
  saldoMoedas: number;
  cupomValeDias: number;
}

export interface ResgateFeito {
  codigo: string;
  expiraEm: string;
  saldoMoedas: number;
  nomeRecompensa: string;
}

export interface Cupom {
  codigo: string;
  nome: string;
  status: string;
  expiraEm: string;
  criadoEm: string;
  /** Quando e em qual caixa foi usado (F3); nulos enquanto não foi. */
  usadoEm: string | null;
  loja: string | null;
  /** O que ele custou em moedas (F4). Zero no cupom da cartela. */
  moedas: number;
}

export interface LinhaExtrato {
  tipo: string;
  moedas: number;
  loja: string | null;
  criadoEm: string;
  /** Quando a moeda desta linha sai da carência; nulo = já disponível. */
  disponivelEm: string | null;
  /** O nome do prêmio nas linhas que apontam para um resgate — a troca e o
      estorno dela (F4). Nulo nas outras. */
  premio: string | null;
}

/**
 * O crédito (cashback) da cliente (F9).
 *
 * ⚠️ Crédito NÃO é moeda: são dois programas, e a loja pode ter os dois. Moeda
 * vira prêmio; crédito vira desconto em reais na próxima compra. Trocar os
 * nomes confunde no caixa, e a tela existe justamente para não trocar.
 */
export type CreditoDaCliente =
  | { usa: false }
  | {
      usa: true;
      /** O que vale hoje, em reais. */
      disponivel: number;
      /** Gerado e ainda esperando a carência. Nunca somado ao de cima. */
      bloqueado: number;
      liberaEm: string | null;
      percentual: number;
      minimoUso: number;
      maximoUso: number;
      /** O desconto sai sozinho no caixa, ou é a lojista que abate no balcão. */
      noCaixa: boolean;
      linhas: LinhaDoCredito[];
    };

export interface LinhaDoCredito {
  tipo: 'entrada' | 'saida';
  valor: number;
  motivo: string;
  dia: string;
  disponivelEm: string | null;
}

export const api = {
  sessao: () => pedir<Sessao>('/sessao'),
  entrar: (telefone: string) => pedir<{ ok: true }>('/entrar', { telefone }),
  codigo: (telefone: string, codigo: string) => pedir<{ ok: true }>('/codigo', { telefone, codigo }),
  entrarQr: (token: string) => pedir<{ ok: true }>('/entrar-qr', { token }),
  sair: () => pedir<{ ok: true }>('/sair', {}),
  carteira: () => pedir<Carteira | CarteiraCartela>('/carteira'),
  lojinha: () => pedir<{ saldoMoedas: number; recompensas: ItemLojinha[] }>('/lojinha'),
  recompensa: (id: string) => pedir<Recompensa>(`/recompensa/${id}`),
  resgatar: (recompensaId: string) => pedir<ResgateFeito>('/resgatar', { recompensaId }),
  cupons: () => pedir<{ ativos: Cupom[]; antigos: Cupom[] }>('/cupons'),
  extrato: () => pedir<{ linhas: LinhaExtrato[] }>('/extrato'),
  /** O crédito (cashback) — módulo separado da moeda (F9). */
  cashback: () => pedir<CreditoDaCliente>('/cashback'),
  regulamento: () => pedir<{ texto: string }>('/regulamento'),
};
